import cx from 'classnames';
import {Link} from 'gatsby';
import Head from 'modules/common/Head';
import LoadingIndicator from 'modules/common/LoadingIndicator';
import ArrowBack from 'modules/common/Svg/ArrowBack';
import Tooltip from 'modules/common/Tooltip';
import TrackTime from 'modules/common/TrackTime';
import PropTypes from 'prop-types';
import React, {lazy, Suspense} from 'react';
import {FormattedMessage} from 'react-intl';
import {compose, lifecycle, withHandlers, withStateHandlers} from 'recompose';
import {sticky} from '../styles.scss';
import Stage from './Stage';
import { useQuery } from '@apollo/client';
import SUBSCRIPTION from '../../graphql/subscription.graphql'
import {
  bgBlue,
  fixedBar,
  paddingBottom,
  sideBarWidth,
  transition,
  translateX0,
  translateX100,
} from './styles.scss';

const SidebarButton = lazy(() => import('modules/common/SidebarButton'));

const StepsSideBar = ({
  courseName,
  courseSlug,
  courseStages,
  isDegree,
  stageSlug,
  stepSlug,
  sidebar,
  stepName,
  track,
  titleFontSize,
  registerTitleBox,
}) => {
  const { data } = useQuery(SUBSCRIPTION);
  return (
  <div
    className={cx(
      'bg-white z-999 vh-100 overflow-hidden right-0',
      sticky,
      transition,
      sideBarWidth,
      {
        [translateX0]: sidebar,
        [translateX100]: !sidebar,
      },
    )}
  >
    <>
      <Head name={stepName} />
      <TrackTime
        messages={[
          isDegree
            ? `Time on degree ${track.name}`
            : `Time on track ${track.name}`,
          `Time on course ${courseName}`,
        ]}
      />
      <div
        className={cx(
          'tr flex justify-between items-center ph3',
          bgBlue,
          fixedBar,
          transition,
        )}
      >
        <Link
          to={
            isDegree
              ? `/degrees/${track.slug}/${courseSlug}`
              : `/${track.slug}/${courseSlug}`
          }
          className="w-100 white b flex items-center f4-ns f6"
        >
          <ArrowBack />
          <div className="ma2 flex1 w-100" style={{fontSize: titleFontSize}}>
            <div ref={registerTitleBox} className="nowrap">
              <Tooltip
                bottomBlack
                message={
                  <span className="f7 white">
                    <FormattedMessage id="back_to_courses_page" />
                  </span>
                }
              >
                {courseName}
              </Tooltip>
            </div>
          </div>
        </Link>
        <Suspense fallback={<LoadingIndicator />}>
          <SidebarButton onSideBar />
        </Suspense>
      </div>
      <div
        className={`flex1 absolute bg-light-gray vh-100 overflow-auto w-100 ${paddingBottom}`}
      >
        {courseStages.map((stage, i) => (
          <Stage
            key={stage.id}
            courseSlug={courseSlug}
            currentStepSlug={stepSlug}
            isCurrent={stageSlug === stage.slug}
            isDegree={isDegree}
            stage={stage}
            stageIndex={i + 1}
            trackSlug={track && track.slug}
            subscription={data?.recurrentPayment?.status}
          />
        ))}
      </div>
    </>
  </div>
)}

StepsSideBar.propTypes = {
  courseName: PropTypes.string.isRequired,
  courseSlug: PropTypes.string.isRequired,
  courseStages: PropTypes.array.isRequired,
  isDegree: PropTypes.bool.isRequired,
  stageSlug: PropTypes.string.isRequired,
  stepSlug: PropTypes.string.isRequired,
  sidebar: PropTypes.bool.isRequired,
  stepName: PropTypes.string.isRequired,
  track: PropTypes.object,
  titleFontSize: PropTypes.string.isRequired,
  registerTitleBox: PropTypes.func.isRequired,
};

const enhance = compose(
  withStateHandlers(
    {
      titleFontSize: `1em`,
    },
    {
      setTitleFontSize: () => (size) => ({
        titleFontSize: size,
      }),
    },
  ),
  withHandlers(() => {
    let titleBox;
    return {
      registerTitleBox: () => (ref) => {
        titleBox = ref;
      },
      adjustTitleBoxFontSize: ({setTitleFontSize}) => () => {
        if (titleBox) {
          const ratio = Math.min(
            1,
            (titleBox.parentElement.offsetWidth - 40) / titleBox.offsetWidth,
          );
          setTitleFontSize(`${ratio}em`);
        }
      },
    };
  }),
  lifecycle({
    componentDidMount() {
      const {closeSidebar, adjustTitleBoxFontSize} = this.props;
      const width =
        window &&
        (window.innerWidth ||
          document.documentElement.clientWidth ||
          document.body.clientWidth);
      if (width < 780) {
        closeSidebar();
      }
      adjustTitleBoxFontSize();
    },
    componentDidUpdate(nextProps) {
      const {courseName, adjustTitleBoxFontSize} = this.props;
      if (courseName && nextProps.courseName !== courseName) {
        adjustTitleBoxFontSize();
      }
    },
  }),
);
export default enhance(StepsSideBar);
